import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import ISTTimeInput from './ist_time_input';
import { BADGES } from './runs_table';
import {
  IST_ZONE,
  epoch_to_ist_wall_clock,
  ist_wall_clock_to_epoch,
  in_zone,
  zone_label
} from './ist_time';

/**
 * One run's editor, rendered inside the Manage modal.
 *
 * ONE SAVE PER RUN: date, duration and every slot commit together, posting the
 * complete slot set. No per-slot saves — those are what produce half-configured
 * runs. Publish is separate, so a Draft can be left half-built safely.
 */

const DURATIONS = [30, 45, 60, 90];

// An 'empty' run is a scheduled one whose slots were all cancelled. It is still
// live and still editable, so slots can be added back.
const is_live = (status) => status === 'scheduled' || status === 'empty';

const is_editable = (status, managing) => status === 'draft' || (is_live(status) && managing);

const to_draft_slots = (slots) =>
  (slots || [])
    .filter((slot) => slot.status === 'active')
    .map((slot) => ({
      slot_uuid: slot.slot_uuid,
      time: epoch_to_ist_wall_clock(moment(slot.start_utc).valueOf()),
      meeting_link: slot.meeting_link || '',
      registration_count: slot.registration_count || 0
    }));

export default function RunCard({
  run,
  onSave,
  onPublish,
  onCancelRun,
  onCancelSlot,
  onDuplicate,
  onDone,
  onDirtyChange,
  embedded,
  busy
}) {
  // In the modal the host already clicked Manage, so a scheduled run starts unlocked.
  const [managing, setManaging] = useState(embedded && is_live(run.status));
  const [run_date, setRunDate] = useState(run.run_date);
  const [duration, setDuration] = useState(run.duration_mins);
  const [slots, setSlots] = useState(() => to_draft_slots(run.slots));
  // Row indexes that the server named in a publish rejection.
  const [flagged, setFlagged] = useState([]);
  const [copied, setCopied] = useState(false);
  // Display only. The slot input below stays on IST regardless — §1.6.
  const zone = useSelector((state) => state.home.iana_timezone) || 'UTC';

  /**
   * Cancelled slots that had a meeting link, for the record only.
   *
   * A cancelled slot with no link was never servable — the landing page requires one,
   * so nothing was ever offered at that time and there is nothing to verify. One that
   * HAD a link may have been shown to visitors, which is worth keeping visible.
   *
   * Kept out of `slots` deliberately: that array is the complete intended set a Save
   * posts, so a cancelled slot in it would be revived on the next Save.
   */
  const removed_slots = (run.slots || []).filter(
    (slot) => slot.status === 'cancelled' && (slot.meeting_link || '').trim()
  );

  // Completed runs start collapsed — read-only, and they'd push the live run off screen.
  const [expanded, setExpanded] = useState(embedded || run.status !== 'completed');

  const editable = is_editable(run.status, managing);
  const badge = BADGES[run.status] || BADGES.draft;

  // Caught before Save so the host sees it on the row, not as a rejected request.
  const collisions = (() => {
    const seen = {};
    const dupes = new Set();
    slots.forEach((slot, i) => {
      if (!slot.time) return;
      if (seen[slot.time] !== undefined) {
        dupes.add(i);
        dupes.add(seen[slot.time]);
      } else {
        seen[slot.time] = i;
      }
    });
    return dupes;
  })();

  const incomplete_rows = slots.reduce(
    (acc, slot, i) => (slot.meeting_link.trim() ? acc : [...acc, i]),
    []
  );
  const has_blank_time = slots.some((slot) => !slot.time);

  const publish_blocked_reason = (() => {
    if (slots.length === 0) return 'Add at least one slot before publishing';
    if (incomplete_rows.length > 0)
      return 'Every slot needs a meeting link before this run can go live';
    if (collisions.size > 0) return 'Two slots share the same time';
    if (has_blank_time) return 'Every slot needs a start time';
    return '';
  })();

  const update_slot = (index, patch) =>
    setSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));

  const add_slot = () =>
    setSlots((prev) => [...prev, { time: '', meeting_link: '', registration_count: 0 }]);

  const remove_slot = (index) => setSlots((prev) => prev.filter((_, i) => i !== index));

  const build_payload = () => ({
    run_date,
    duration_mins: duration,
    // The complete intended set, not a delta: omitted = cancelled, no uuid = created.
    slots: slots.map((slot) => ({
      ...(slot.slot_uuid ? { slot_uuid: slot.slot_uuid } : {}),
      start_time: ist_wall_clock_to_epoch(run_date, slot.time),
      meeting_link: slot.meeting_link.trim() || null
    }))
  });

  // onSave/onPublish contract: null means success, an array is the rejected slot uuids.
  const rows_for = (offending_uuids) =>
    slots.reduce(
      (rows, slot, i) => (offending_uuids.includes(slot.slot_uuid) ? [...rows, i] : rows),
      []
    );

  const apply_outcome = (offending_uuids) => {
    if (offending_uuids === null) {
      setFlagged([]);
      return true;
    }
    // A rejection that names no slot still shows its message as a toast; there is
    // simply no row to point at.
    setFlagged(rows_for(offending_uuids));
    return false;
  };

  const save = async () => {
    if (collisions.size > 0 || has_blank_time) return;
    if (apply_outcome(await onSave(run, build_payload()))) {
      setManaging(false);
      onDone();
    }
  };

  // Publish judges the saved run, so unsaved edits have to land first. Otherwise the
  // server rules on state the host can no longer see and reports a confusing error.
  const dirty =
    run_date !== run.run_date ||
    duration !== run.duration_mins ||
    JSON.stringify(
      slots.map(({ slot_uuid, time, meeting_link }) => [slot_uuid, time, meeting_link])
    ) !==
      JSON.stringify(
        to_draft_slots(run.slots).map(({ slot_uuid, time, meeting_link }) => [
          slot_uuid,
          time,
          meeting_link
        ])
      );

  // The owner needs this: its destructive actions refetch the topic, which remounts this
  // card and resets every field below. Reported up so those confirmations can say so
  // rather than discarding the host's typing silently.
  useEffect(() => {
    onDirtyChange(dirty);

    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  const publish = async () => {
    if (collisions.size > 0 || has_blank_time) return;
    // reload: false — the save must NOT refetch the topic here. That bumps the parent's
    // sync_key, which remounts this card mid-sequence, and the setFlagged below would
    // then be applied to an instance nobody is looking at: a publish rejected for two
    // named slots would outline no rows at all. The publish call does its own refetch.
    if (dirty && !apply_outcome(await onSave(run, build_payload(), { reload: false }))) return;
    if (apply_outcome(await onPublish(run))) onDone();
  };

  return (
    <div
      className={embedded ? '' : 'rounded-lg mb-4'}
      style={
        embedded
          ? { opacity: run.status === 'cancelled' ? 0.6 : 1 }
          : {
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              boxShadow: 'var(--shadow-card)',
              opacity: run.status === 'cancelled' ? 0.6 : 1
            }
      }
    >
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-default)' }}
      >
        {editable ? (
          <input
            type="date"
            value={run_date}
            onChange={(e) => setRunDate(e.target.value)}
            className="px-2 py-1 rounded text-sm"
            style={{
              width: 170,
              flex: '0 0 auto',
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)'
            }}
          />
        ) : (
          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
            {moment(run.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
          </span>
        )}

        {editable ? (
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="px-2 py-1 rounded text-sm"
            style={{
              width: 120,
              flex: '0 0 auto',
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)'
            }}
          >
            {DURATIONS.map((mins) => (
              <option key={mins} value={mins}>
                {mins} min
              </option>
            ))}
          </select>
        ) : (
          <span style={{ color: 'var(--text-secondary)' }}>· {run.duration_mins} min</span>
        )}

        <span
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ background: badge.bg, color: badge.fg }}
        >
          {badge.label}
        </span>

        {!embedded && (
          <div className="ml-auto flex gap-2">
            {is_live(run.status) && !managing && (
              <button type="button" className="ct-small-btn" onClick={() => setManaging(true)}>
                Manage
              </button>
            )}
            {run.status !== 'cancelled' && (
              <>
                <button type="button" className="ct-small-btn" onClick={() => onDuplicate(run)}>
                  Duplicate
                </button>
                <button type="button" className="ct-small-btn" onClick={() => onCancelRun(run)}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* The run's external identifier, on published runs only — a draft has never
          served anything, so there is nothing to go looking for. Query with:
          SELECT * FROM event_run WHERE uuid = '<this>'; joins use event_run.id. */}
      {run.status !== 'draft' && (
        <div
          className="flex items-center gap-2 px-4 py-2 text-xs"
          style={{
            borderBottom: '1px solid var(--border-default)',
            background: 'var(--surface-sunken)',
            color: 'var(--text-muted)'
          }}
        >
          <span className="font-semibold uppercase tracking-wide">Run ID</span>
          <code
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: 'var(--text-secondary)',
              userSelect: 'all'
            }}
          >
            {run.run_uuid}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(run.run_uuid);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            title="Copy the run uuid"
            aria-label="Copy the run uuid"
            // common.css styles every bare button as a 14px orange pill, so anything
            // that should read as plain text or a link has to reset it explicitly.
            style={{
              background: copied ? 'var(--state-success-bg)' : 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              borderRadius: 4,
              padding: '1px 6px',
              fontSize: 11,
              lineHeight: '16px',
              color: copied ? 'var(--state-success-text)' : 'var(--state-info)',
              whiteSpace: 'nowrap'
            }}
          >
            {copied ? '\u2713 Copied' : '\u29C9 Copy'}
          </button>
        </div>
      )}

      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full text-left px-4 py-3 text-sm"
          style={{
            color: 'var(--text-muted)',
            background: 'transparent',
            border: 'none',
            borderRadius: 0
          }}
        >
          {slots.length} {slots.length === 1 ? 'slot' : 'slots'} · show
        </button>
      )}

      {expanded && (
        <div className="px-4 py-3">
          <div
            className="grid gap-3 text-xs uppercase pb-2"
            style={{ gridTemplateColumns: '150px 175px 1fr 140px', color: 'var(--text-muted)' }}
          >
            <div>Time (IST)</div>
            <div>{zone_label(zone)}</div>
            <div>Meeting link</div>
            <div />
          </div>

          {slots.length === 0 && (
            <div className="py-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              No slots yet.
            </div>
          )}

          {slots.map((slot, index) => {
            const epoch = slot.time ? ist_wall_clock_to_epoch(run_date, slot.time) : null;
            const link_missing = !slot.meeting_link.trim();
            const row_flagged = flagged.includes(index) || collisions.has(index);

            return (
              <div
                // Unsaved rows have no uuid yet, so the index is their only stable key.
                key={slot.slot_uuid || `new-${index}`}
                className="grid gap-3 items-start py-2"
                style={{
                  gridTemplateColumns: '150px 175px 1fr 140px',
                  borderTop: '1px solid var(--border-default)'
                }}
              >
                <ISTTimeInput
                  run_date={run_date}
                  value={slot.time}
                  onChange={(time) => update_slot(index, { time })}
                  disabled={!editable}
                  invalid={row_flagged}
                />

                <div
                  className="text-sm pt-1 whitespace-nowrap"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {epoch !== null ? in_zone(epoch, zone, run_date) : '—'}
                </div>

                <div>
                  {/* Editable in every state — a wrong link is the thing a host most needs to fix. */}
                  <input
                    type="url"
                    value={slot.meeting_link}
                    placeholder="https://zoom.us/j/…"
                    onChange={(e) => update_slot(index, { meeting_link: e.target.value })}
                    className="w-full px-2 py-1 rounded text-sm"
                    style={{
                      background: 'var(--input-bg)',
                      border: `1px solid ${row_flagged || link_missing ? 'var(--state-warning)' : 'var(--input-border)'}`,
                      color: 'var(--input-text)'
                    }}
                  />
                  {link_missing && (
                    <div className="mt-1 text-xs" style={{ color: 'var(--state-warning-text)' }}>
                      ⚠ Incomplete — no meeting link
                    </div>
                  )}
                  {collisions.has(index) && (
                    <div className="mt-1 text-xs" style={{ color: 'var(--state-error-text)' }}>
                      Two slots cannot share the same time
                    </div>
                  )}
                </div>

                <div
                  className="flex items-center gap-2 pt-1 text-sm"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {slot.registration_count > 0 && (
                    <span>👤 {slot.registration_count} registered</span>
                  )}
                  {/* A registered slot cannot be removed, but it can be cancelled. */}
                  {editable && slot.registration_count > 0 && slot.slot_uuid && (
                    <button
                      type="button"
                      className="ct-small-btn"
                      onClick={() => onCancelSlot(slot)}
                    >
                      Cancel slot
                    </button>
                  )}
                  {editable && slot.registration_count === 0 && (
                    <button
                      type="button"
                      aria-label="Remove slot"
                      onClick={() => remove_slot(index)}
                      style={{
                        color: 'var(--text-muted)',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 0,
                        padding: '0 8px'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {editable && (
            <button
              type="button"
              onClick={add_slot}
              className="mt-2 text-sm"
              style={{
                color: 'var(--state-info)',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                padding: 0
              }}
            >
              + Add slot
            </button>
          )}

          {removed_slots.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: 'var(--text-muted)' }}
              >
                Removed
              </div>
              {removed_slots.map((slot) => (
                <div
                  key={slot.slot_uuid}
                  className="grid gap-3 py-1 text-sm"
                  style={{ gridTemplateColumns: '150px 175px 1fr', color: 'var(--text-muted)' }}
                >
                  <span style={{ textDecoration: 'line-through' }}>
                    {moment(slot.start_utc).tz(IST_ZONE).format('h:mm A')}
                  </span>
                  <span className="whitespace-nowrap">
                    {in_zone(slot.start_utc, zone, run.run_date)}
                  </span>
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={slot.meeting_link || ''}
                  >
                    {slot.meeting_link || '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editable && (
        <div
          className="flex justify-end gap-2 px-4 py-3"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          {managing && (
            <button
              type="button"
              className="ct-small-btn"
              onClick={() => setManaging(false)}
              disabled={busy}
            >
              Done
            </button>
          )}
          <button
            type="button"
            className="ct-small-btn"
            onClick={save}
            disabled={busy || collisions.size > 0 || has_blank_time}
          >
            Save
          </button>
          {run.status === 'draft' && (
            <button
              type="button"
              onClick={publish}
              disabled={busy || !!publish_blocked_reason}
              title={publish_blocked_reason}
              className="px-3 py-1 rounded text-sm font-medium"
              style={{
                background: publish_blocked_reason
                  ? 'var(--border-emphasis)'
                  : 'var(--brand-primary)',
                color: 'var(--text-inverse)',
                cursor: publish_blocked_reason ? 'not-allowed' : 'pointer'
              }}
            >
              Publish run
            </button>
          )}
        </div>
      )}
    </div>
  );
}

RunCard.propTypes = {
  run: PropTypes.shape({
    run_uuid: PropTypes.string,
    run_date: PropTypes.string,
    duration_mins: PropTypes.number,
    status: PropTypes.string,
    slots: PropTypes.arrayOf(
      PropTypes.shape({
        slot_uuid: PropTypes.string,
        start_utc: PropTypes.string,
        meeting_link: PropTypes.string,
        status: PropTypes.string,
        registration_count: PropTypes.number
      })
    )
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onCancelRun: PropTypes.func.isRequired,
  onCancelSlot: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  onDirtyChange: PropTypes.func,
  embedded: PropTypes.bool,
  busy: PropTypes.bool
};

RunCard.defaultProps = {
  onDone: () => {},
  onDirtyChange: () => {},
  embedded: false,
  busy: false
};
